import { z } from "zod";

import type { Auth } from "./auth.ts";
import type { ApiClient } from "./client.ts";

const sessionSchema = z
  .object({
    id: z.uuid(),
    user_agent: z.string().nullable(),
    created_at: z.string(),
    last_active_at: z.string(),
    shared: z.boolean(),
    is_current: z.boolean(),
  })
  .transform((row) => ({
    id: row.id,
    userAgent: row.user_agent ?? "",
    createdAt: row.created_at,
    lastActiveAt: row.last_active_at,
    isShared: row.shared,
    isCurrent: row.is_current,
  }));

export type DeviceSession = z.infer<typeof sessionSchema>;

export interface Sessions {
  readonly list: () => Promise<readonly DeviceSession[]>;
  readonly end: (id: string) => Promise<void>;
  readonly endOthers: () => Promise<void>;
  readonly setShared: (isShared: boolean) => Promise<void>;
}

export interface SessionKeeper {
  readonly start: () => void;
  readonly stop: () => void;
  readonly setShared: (isShared: boolean) => void;
}

const touchInterval = 60 * 1000;
const trustedLimit = 12 * 60 * 60 * 1000;
const sharedLimit = 30 * 60 * 1000;
const activityEvents = ["pointerdown", "keydown", "visibilitychange"] as const;

const orThrow = (error: Error | null): void => {
  if (error !== null) {
    throw error;
  }
};

export const createSessions = (client: ApiClient, keeper: SessionKeeper): Sessions => ({
  list: async () => {
    const { data, error } = await client.db.rpc("sessions");
    orThrow(error);
    return z.array(sessionSchema).parse(data);
  },
  end: async (id) => {
    const { error } = await client.db.rpc("end_session", { target: id });
    orThrow(error);
  },
  endOthers: async () => {
    const { error } = await client.db.rpc("end_other_sessions");
    orThrow(error);
  },
  setShared: async (isShared) => {
    const { error } = await client.db.rpc("set_shared_device", { is_shared: isShared });
    orThrow(error);
    keeper.setShared(isShared);
  },
});

export const createSessionKeeper = (client: ApiClient, auth: Auth): SessionKeeper => {
  let isRunning = false;
  let lastTouch = 0;
  let limit = trustedLimit;
  let lock: ReturnType<typeof setTimeout> | undefined;
  const expire = (): void => {
    void auth.signOut("security");
  };
  const armLock = (): void => {
    clearTimeout(lock);
    lock = setTimeout(expire, limit);
  };
  const touch = async (): Promise<void> => {
    lastTouch = Date.now();
    const { data, error } = await client.db.rpc("touch_session");
    if (error?.code === "42501") {
      expire();
      return;
    }
    orThrow(error);
    limit = data === true ? sharedLimit : trustedLimit;
    armLock();
  };
  const onActivity = (): void => {
    if (document.visibilityState === "hidden") {
      return;
    }
    armLock();
    if (Date.now() - lastTouch >= touchInterval) {
      void touch();
    }
  };
  return {
    start: () => {
      if (isRunning) {
        return;
      }
      isRunning = true;
      for (const event of activityEvents) {
        addEventListener(event, onActivity, { passive: true });
      }
      void touch();
    },
    stop: () => {
      isRunning = false;
      for (const event of activityEvents) {
        removeEventListener(event, onActivity);
      }
      clearTimeout(lock);
      lastTouch = 0;
    },
    setShared: (isShared) => {
      limit = isShared ? sharedLimit : trustedLimit;
      armLock();
    },
  };
};
